import React, { useEffect, useState } from "react";
import { AiOutlineMenu } from "react-icons/ai";
import { Avatar, Button, Space, Modal, Select, message } from "antd";
import {
  UserOutlined,
  CoffeeOutlined,
  RestOutlined,
  LoginOutlined,
  DashboardOutlined,
  TeamOutlined,
  SettingOutlined,
  FieldTimeOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { BsChatLeft } from "react-icons/bs";
import { RiNotification3Line } from "react-icons/ri";
import { MdKeyboardArrowDown, MdNotificationsActive, MdNotificationsOff } from "react-icons/md";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import {
  Cart,
  Chat,
  Notification,
  UserProfile,
  WebSocketNotification,
  EmployeeStatusInline,
  CompanyPortal,
} from ".";
import { useStateContext } from "../contexts/ContextProvider";
import {
  startBreak,
  endBreak,
  getBreakStatus,
  checkIn,
  checkOut,
  getUserStatus,
  punchClock,
  getTimeClockStatus,
} from "../services/breakService";
import websocketService from "../services/websocket";
import { apiCache } from "../utils/apiCache";
import { updateCompanyIdHeader } from "../services/index";

import { useFetchCompanyForNavbar } from "../modules/back-office/Companies/hooks/useFetch";
import { useMyCompanies } from "../hooks/useMyCompanies";
import {
  isSoundEnabled,
  setSoundEnabled,
  unlockAudio,
  playNewWorkSound,
  installAutoUnlock,
  isAudioReady,
} from "../services/notificationSound";

const NavButton = ({ title, customFunc, icon, color, dotColor }) => (
  <TooltipComponent content={title} position="BottomCenter">
    <button
      type="button"
      onClick={() => customFunc()}
      style={{ color }}
      className="relative text-xl rounded-full p-3 hover:bg-light-gray"
    >
      <span
        style={{ background: dotColor }}
        className="absolute inline-flex rounded-full h-2 w-2 right-2 top-2"
      />
      {icon}
    </button>
  </TooltipComponent>
);

const Navbar = () => {
  const {
    currentColor,
    activeMenu,
    setActiveMenu,
    handleClick,
    isClicked,
    setScreenSize,
    screenSize,
    getProfile,
  } = useStateContext();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [isCompanyModalVisible, setIsCompanyModalVisible] = useState(false);
  const [isPortalVisible, setIsPortalVisible] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyName, setCompanyName] = useState(null);
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  // เสียงพร้อมเล่นจริงหรือยัง — ต่างจาก soundOn ตรงที่อันนี้รีเซ็ตทุกครั้งที่โหลดหน้า
  const [audioReady, setAudioReady] = useState(isAudioReady());
  const [modalType, setModalType] = useState("");
  const [userName, setUserName] = useState("");

  // Break status states (เฉพาะ lunch)
  const [lunchBreakStatus, setLunchBreakStatus] = useState({
    isOnBreak: false,
    userId: null,
    userName: null,
    type: null,
    startTime: null,
  });
  const [isLunchBreakLoading, setIsLunchBreakLoading] = useState(false);

  // Individual break states
  const [activeBreaks, setActiveBreaks] = useState(new Set()); // เก็บ userId ที่กำลังพัก coffee
  const [isBreakLoading, setIsBreakLoading] = useState(false);
  const [canCheckOut, setCanCheckOut] = useState(false);
  // Work status states
  const [workStatus, setWorkStatus] = useState(null); // Ready, Dayoff, null (loading)
  const [activeStatus, setActiveStatus] = useState(null); // Ready, Break, LunchBreak, Dayoff, null (loading)
  const [isWorkLoading, setIsWorkLoading] = useState(false);
  const [isScheduled, setIsScheduled] = useState(false);
  // Time clock (ตอกบัตร) states
  const [clockState, setClockState] = useState(null); // 'none' | 'in' | 'done' | null (loading)
  const [isPunching, setIsPunching] = useState(false);
  const { data: dataCompany } = useFetchCompanyForNavbar();
  // บริษัทที่ user คนนี้เข้าถึงได้ (คนละชุดกับ dataCompany ซึ่งเป็น "ทุกบริษัทในระบบ")
  const { data: myCompanies = [] } = useMyCompanies();

  // แท็บที่ portal เพิ่งเปิดจะยังไม่มีชื่อ/สีบริษัท (ตั้งใจล้างทิ้งกันโชว์ของบริษัทเดิม)
  // พอรายชื่อมาถึงก็เติมให้ตรงกับ activeCompanyId ของแท็บนี้
  useEffect(() => {
    if (!myCompanies.length) return;
    const activeId = sessionStorage.getItem("activeCompanyId");
    if (!activeId || sessionStorage.getItem("companyName")) return;
    const found = myCompanies.find((c) => String(c.id) === String(activeId));
    if (!found) return;
    sessionStorage.setItem("companyName", found.companyName || "");
    if (found.themeColor) sessionStorage.setItem("companyColor", found.themeColor);
    setCompanyName(found.companyName || "");
  }, [myCompanies]);
  const userRole = sessionStorage.getItem("role");
  const currentUserId = parseInt(sessionStorage.getItem("id") || 0);
  const currentUserName = sessionStorage.getItem("userName");

  const companies = dataCompany
    ? dataCompany.map((company) => ({
      value: company.id,
      label: company.companyName,
    }))
    : [];

  // เคยเปิดเสียงไว้ → ปลดล็อกอัตโนมัติตอนผู้ใช้คลิกอะไรก็ได้ครั้งแรก
  // ไม่ต้องมากดปิด-เปิดกระดิ่งใหม่ทุกครั้งที่ refresh หน้า
  useEffect(() => {
    if (isSoundEnabled() && !isAudioReady()) {
      installAutoUnlock(() => setAudioReady(true));
    }
  }, []);

  // เปิด/ปิดเสียงแจ้งเตือน — เก็บใน localStorage จะได้ไม่ต้องตั้งใหม่ทุกครั้งที่ login
  const handleToggleSound = async () => {
    const next = !soundOn;
    setSoundEnabled(next);
    setSoundOn(next);
    if (next) {
      // ต้องปลดล็อกภายใน onClick นี้เท่านั้น (autoplay policy)
      const ok = await unlockAudio();
      setAudioReady(ok);
      if (ok) {
        playNewWorkSound(); // ให้ได้ยินตัวอย่างว่าเสียงเป็นยังไง + ยืนยันว่าใช้ได้จริง
        message.success("เปิดเสียงแจ้งเตือนงานใหม่แล้ว");
      } else {
        setSoundEnabled(false);
        setSoundOn(false);
        message.warning("เบราว์เซอร์นี้เล่นเสียงไม่ได้");
      }
    } else {
      message.info("ปิดเสียงแจ้งเตือนแล้ว");
    }
  };

  const handleNavigation = (type) => {
    const activeCompanyId = sessionStorage.getItem("activeCompanyId");

    switch (type) {
      case "backoffice":
        sessionStorage.removeItem("activeCompanyId");
        sessionStorage.removeItem("companyName");
        sessionStorage.removeItem("companyColor");
        navigate("/backoffice");
        break;
      case "workspace":
        if (activeCompanyId && activeCompanyId != "0") {
          navigate("/workspace");
        } else {
          setModalType("workspace");
          setIsCompanyModalVisible(true);
        }
        break;
      case "admin":
        if (activeCompanyId && activeCompanyId != "0") {
          navigate("/admin");
        } else {
          setModalType("admin");
          setIsCompanyModalVisible(true);
        }
        break;
      default:
        break;
    }
    sessionStorage.setItem("location", type);
  };

  const handleCompanySelect = () => {
    if (selectedCompany) {
      const previousCompany = sessionStorage.getItem("activeCompanyId");
      sessionStorage.setItem("activeCompanyId", selectedCompany);
      sessionStorage.setItem("companyName", companyName);
      // สีประจำบริษัท — sidebar เอาไปทาชื่อบริษัท ให้แยกออกทันทีว่าอยู่บริษัทไหน
      const picked = (dataCompany || []).find(
        (c) => String(c.id) === String(selectedCompany)
      );
      if (picked?.themeColor) {
        sessionStorage.setItem("companyColor", picked.themeColor);
      } else {
        sessionStorage.removeItem("companyColor");
      }
      updateCompanyIdHeader(selectedCompany);

      // ล้าง cache ทั้งหมดเมื่อเปลี่ยนบริษัท — query key ส่วนใหญ่ไม่มี companyId อยู่ในนั้น
      // (errorTopics / blacklistTopics / get-profile ฯลฯ) react-query จึงคืนของบริษัทเดิม
      // จาก cache แล้วไม่ยิงใหม่ ทำให้เห็นข้อมูลผิดบริษัทได้นานถึง staleTime (30 วิ)
      // ใช้ clear() แทนการไล่ใส่ companyId ทีละคีย์ — ครอบคลุมทุก query โดยไม่ต้องจำว่ามีอะไรบ้าง
      if (String(previousCompany) !== String(selectedCompany)) {
        queryClient.clear();
        apiCache.clear();
      }
      if (modalType === "workspace") {
        navigate("/workspace");
      } else if (modalType === "admin") {
        navigate("/admin");
      }
      setIsCompanyModalVisible(false);
      setSelectedCompany(null);
      setCompanyName(null);
    }
  };

  useEffect(() => {
    const handleResize = () => setScreenSize(window.innerWidth);

    window.addEventListener("resize", handleResize);

    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (screenSize <= 900) {
      setActiveMenu(false);
    } else {
      setActiveMenu(true);
    }
  }, [screenSize]);

  useEffect(() => {
    const storedUserName = sessionStorage.getItem("userName");
    if (storedUserName) {
      setUserName(storedUserName);
    }
  }, []);

  // Setup WebSocket listeners และ fetch break status
  useEffect(() => {
    // ดึงสถานะ break ปัจจุบัน (debounce)
    setTimeout(() => fetchBreakStatus(), 500);

    // ดึงสถานะของผู้ใช้ปัจจุบัน (debounce)
    setTimeout(() => fetchUserStatus(), 1000);

    // Setup WebSocket listeners (อัพเดททุกครั้งที่มีการเปลี่ยนแปลง)
    websocketService.onBreakEvent((eventData) => {
      // Clear cache และอัปเดตทุกครั้งที่มีการเปลี่ยนแปลง break status
      apiCache.clear();

      if (eventData.type === "started") {
        if (eventData.breakType === "lunch") {
          setLunchBreakStatus({
            isOnBreak: true,
            userId: eventData.userId,
            userName: eventData.userName,
            type: eventData.breakType,
            startTime: eventData.startTime,
          });
        } else if (eventData.breakType === "coffee") {
          setActiveBreaks((prev) =>
            new Set(prev).add(parseInt(eventData.userId))
          );
        }
      } else if (eventData.type === "ended") {
        if (eventData.breakType === "lunch") {
          setLunchBreakStatus({
            isOnBreak: false,
            userId: null,
            userName: null,
            type: null,
            startTime: null,
          });
        } else if (eventData.breakType === "coffee") {
          setActiveBreaks((prev) => {
            const newSet = new Set(prev);
            newSet.delete(parseInt(eventData.userId));
            return newSet;
          });
        }
      }

      // อัปเดตสถานะผู้ใช้ปัจจุบันด้วย
      setTimeout(() => fetchUserStatus(), 500);
    });

    // Setup WebSocket listeners for work events (อัพเดททุกครั้ง)
    websocketService.onWorkEvent((eventData) => {
      // Clear cache และอัปเดตสถานะทุกครั้งที่มีการเข้า/ออกงาน
      apiCache.clear();

      if (eventData.userId === currentUserId.toString()) {
        if (eventData.type === "checkin") {
          setWorkStatus("Ready");
        } else if (eventData.type === "checkout") {
          setWorkStatus("Dayoff");
        }
      }

      // อัปเดตสถานะผู้ใช้ปัจจุบันด้วย
      setTimeout(() => fetchUserStatus(), 500);
    });

    return () => {
      // Cleanup listeners ถ้าจำเป็น
    };
  }, [currentUserId]);

  const handleActiveMenu = () => setActiveMenu(!activeMenu);

  // ดึงสถานะ lunch break ปัจจุบัน
  const fetchBreakStatus = async () => {
    try {
      const response = await getBreakStatus();
      if (response.success) {
        setLunchBreakStatus({
          isOnBreak: response.result.isOnBreak,
          userId: response.result.userId,
          userName: response.result.userName,
          type: response.result.type,
          startTime: response.result.startTime,
        });
      }
    } catch (error) {
      console.error("Error fetching break status:", error);
    }
  };

  // ดึงสถานะของผู้ใช้ปัจจุบัน
  const fetchUserStatus = async () => {
    try {
      if (currentUserId) {
        const response = await getUserStatus(currentUserId.toString());
        if (response.success) {
          const status = response.result.status;
          const isScheduled = response.result.isScheduled;
          const canCheckOut = response.result.canCheckOut;
          // แปลงสถานะเป็น workStatus
          if (
            status === "Ready" ||
            status === "Break" ||
            status === "LunchBreak"
          ) {
            setActiveStatus(status);
            setWorkStatus("Ready");
            setIsScheduled(isScheduled);
            setCanCheckOut(canCheckOut);
          } else {
            setActiveStatus(status);
            setWorkStatus("Dayoff");
            setIsScheduled(isScheduled);
            setCanCheckOut(canCheckOut);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user status:", error);
      setWorkStatus("Dayoff"); // กรณีเกิดข้อผิดพลาด ให้เป็น Dayoff
      setIsScheduled(false);
      setCanCheckOut(false);
    }
  };

  // จัดการเริ่มพัก
  const handleStartBreak = async (type) => {
    if (!currentUserId || !currentUserName) {
      message.error("ไม่พบข้อมูลผู้ใช้");
      return;
    }

    if (type === "lunch") {
      setIsLunchBreakLoading(true);
    } else {
      setIsBreakLoading(true);
    }

    try {
      const response = await startBreak({
        userId: currentUserId.toString(),
        userName: currentUserName,
        type: type,
      });

      if (response.success) {
        const typeText =
          type === "lunch"
            ? "LunchBreak"
            : type === "coffee"
              ? "Break"
              : "เข้างาน";
        message.success(`เริ่ม${typeText}แล้ว`);

        // Clear cache เพื่อให้ข้อมูลอัพเดททันที
        apiCache.clear();

        if (type === "lunch") {
          const newLunchBreakStatus = {
            isOnBreak: response.result.isOnLunchBreak,
            userId: response.result.userId,
            userName: response.result.userName,
            type: "lunch",
            startTime: response.result.startTime,
          };
          setLunchBreakStatus(newLunchBreakStatus);
        } else if (type === "coffee") {
          setActiveBreaks((prev) => new Set(prev).add(parseInt(currentUserId)));
        }

        // Redirect ไปหน้า Dashboard หลังจากเริ่มพัก
        setTimeout(() => {
          window.location.href = "/workspace";
        }, 1000);
      }
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error("เกิดข้อผิดพลาดในการเริ่มพัก");
      }
    } finally {
      if (type === "lunch") {
        setIsLunchBreakLoading(false);
      } else {
        setIsBreakLoading(false);
      }
    }
  };

  // จัดการจบพัก
  const handleEndBreak = async (type) => {
    if (!currentUserId) {
      message.error("ไม่พบข้อมูลผู้ใช้");
      return;
    }

    if (type === "lunch") {
      setIsLunchBreakLoading(true);
    } else {
      setIsBreakLoading(true);
    }

    try {
      const response = await endBreak(currentUserId.toString(), type);
      if (response.success) {
        const typeText =
          type === "lunch"
            ? "LunchBreak"
            : type === "coffee"
              ? "Break"
              : "เข้างาน";
        message.success(`จบ${typeText}แล้ว`);

        // Clear cache เพื่อให้ข้อมูลอัพเดททันที
        apiCache.clear();

        if (type === "lunch") {
          setLunchBreakStatus({
            isOnBreak: false,
            userId: null,
            userName: null,
            type: null,
            startTime: null,
          });
        } else if (type === "coffee") {
          setActiveBreaks((prev) => {
            const newSet = new Set(prev);
            newSet.delete(parseInt(currentUserId));
            return newSet;
          });
        }

        // Redirect ไปหน้า Dashboard หลังจากจบพัก
        setTimeout(() => {
          window.location.href = "/workspace";
        }, 1000);
      }
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error("เกิดข้อผิดพลาดในการจบพัก");
      }
    } finally {
      if (type === "lunch") {
        setIsLunchBreakLoading(false);
      } else {
        setIsBreakLoading(false);
      }
    }
  };

  // จัดการเข้างาน
  const handleCheckIn = async () => {
    if (!currentUserId) {
      message.error("ไม่พบข้อมูลผู้ใช้");
      return;
    }

    setIsWorkLoading(true);
    try {
      const response = await checkIn(currentUserId.toString());
      if (response.success) {
        message.success("ออนไลน์แล้ว");
        setWorkStatus("Ready");
        // Clear cache เพื่อให้ข้อมูลอัพเดททันที
        apiCache.clear();
        // Redirect ไปหน้า Dashboard หลังจากเข้างาน
        setTimeout(() => {
          window.location.href = "/workspace";
        }, 1000);
      }
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error("เกิดข้อผิดพลาดในการออนไลน์");
      }
    } finally {
      setIsWorkLoading(false);
    }
  };

  // จัดการออกงาน
  // reason: 'offline' = กดปุ่มออฟไลน์ (พักชั่วคราว) → เคสถูกกระจายต่อให้คนอื่นที่ทำ task เดียวกัน
  //         'clock_out' = มาจากตอกบัตรออกงาน → เคสลงกล่องกลางเหมือนเดิม
  // redirect = false ใช้ตอนตอกบัตรออกงาน เพราะจะ logout ต่อทันที
  // (ปล่อยให้ redirect ทำงานด้วยจะชนกัน — เด้งไปหน้าหนึ่งแล้วโดนพากลับมาหน้า login)
  // คืน true เมื่อออฟไลน์สำเร็จ ผู้เรียกจะได้รู้ว่าทำต่อได้ไหม
  const handleCheckOut = async (reason = "clock_out", { redirect = true } = {}) => {
    if (!currentUserId) {
      message.error("ไม่พบข้อมูลผู้ใช้");
      return false;
    }

    setIsWorkLoading(true);
    try {
      const response = await checkOut(currentUserId.toString(), reason);

      // ⚠️ backend ตอบ success ได้ทั้งที่ "ไม่ได้ทำอะไรเลย" (result.skipped)
      //    เช่นตอนที่ระบบมองว่าไม่ได้อยู่ในช่วงตอกบัตรเข้างาน
      //    ถ้าเชื่อ success ดื้อ ๆ ปุ่มจะพลิกเป็น "ออนไลน์" เหมือนออฟไลน์สำเร็จ
      //    แต่ status บนเซิร์ฟเวอร์ยังเป็น Ready → เคสเด้งเข้าต่อโดยที่คนกดไม่รู้ตัว
      //    (ไม่มี polling ด้วย ปุ่มจึงค้างหลอกตาไปทั้ง session)
      if (response.success && response.result?.skipped) {
        message.warning(
          response.message || "ออฟไลน์ไม่สำเร็จ — ระบบไม่พบการตอกบัตรเข้างานของคุณ"
        );
        await fetchUserStatus();   // ดึงสถานะจริงจากเซิร์ฟเวอร์กลับมาแสดง
        await fetchClockStatus();
        return false;
      }

      if (response.success) {
        message.success("ออฟไลน์แล้ว");
        setWorkStatus("Dayoff");
        // Clear cache เพื่อให้ข้อมูลอัพเดททันที
        apiCache.clear();
        // Redirect ไปหน้า Dashboard หลังจากออกงาน
        if (redirect) {
          setTimeout(() => {
            window.location.href = "/workspace";
          }, 1000);
        }
        return true;
      }
      return false;
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error("เกิดข้อผิดพลาดในการออฟไลน์");
      }
      return false;
    } finally {
      setIsWorkLoading(false);
    }
  };

  // ออกจากระบบ — ใช้ชุดเดียวกับปุ่ม Logout ที่ UserProfile
  // sessionStorage เป็นของแท็บใครแท็บมัน → ตอกออกที่บริษัท A ไม่เตะแท็บบริษัท B ออก
  // ใช้ window.location แทน navigate เพื่อโหลดใหม่ทั้งหน้า (ล้าง react-query cache + state ในหน่วยความจำ)
  const doLogout = () => {
    websocketService.disconnect();
    apiCache.clear();
    sessionStorage.clear();
    window.location.href = "/";
  };

  // ===== ตอกบัตร (Time clock) =====
  const fetchClockStatus = async () => {
    try {
      const response = await getTimeClockStatus();
      if (response.success) {
        setClockState(response.result?.state || "none");
      } else {
        setClockState("none");
      }
    } catch (error) {
      console.error("Error fetching time clock status:", error);
      setClockState("none");
    }
  };

  useEffect(() => {
    if (currentUserId && userRole === "3") {
      fetchClockStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId]);

  // กดตอกบัตร: แสดง popup ยืนยันก่อน แล้วค่อยตอกจริง
  const handlePunch = () => {
    if (!currentUserId) {
      message.error("ไม่พบข้อมูลผู้ใช้");
      return;
    }
    if (isPunching) return; // กัน double-click

    const isClockOut = clockState === "in"; // ตอกเข้าค้างอยู่ → กดครั้งนี้คือตอกออก
    Modal.confirm({
      title: isClockOut ? "ยืนยันการออกงาน" : "ยืนยันการเข้างาน",
      content: isClockOut
        ? "ยืนยันการออกงานใช่หรือไม่? ถ้าออกงานแล้วจะไม่สามารถเข้างานได้อีกในวันนี้ และระบบจะออกจากระบบให้อัตโนมัติ"
        : "ยืนยันการเข้างานใช่หรือไม่? ระบบจะบันทึกเวลาเข้างานของคุณ",
      okText: isClockOut ? "ยืนยันออกงาน" : "ยืนยันเข้างาน",
      cancelText: "ยกเลิก",
      okButtonProps: isClockOut ? { danger: true } : undefined,
      // return promise → ปุ่มยืนยันจะขึ้น loading จนกว่าจะตอกเสร็จ
      onOk: () => doPunch(isClockOut),
    });
  };

  // ตอกจริงหลังยืนยัน: ตอกเข้า → ออนไลน์ให้อัตโนมัติ / ตอกออก → ออฟไลน์ให้อัตโนมัติ
  const doPunch = async (isClockOut) => {
    setIsPunching(true);
    try {
      if (isClockOut) {
        const res = await punchClock("out");
        if (res.success) {
          setClockState("done");
          message.success("ตอกบัตรออกงานแล้ว");
          // ตอกออก = จบกะ → เคสลงกล่องกลาง (ไม่กระจายต่อ เพราะกะนี้จบแล้ว)
          const wentOffline = await handleCheckOut("clock_out", { redirect: false });
          if (wentOffline) {
            // จบกะแล้ว → ออกจากระบบให้เลย
            message.success("ออกงานเรียบร้อย กำลังออกจากระบบ...");
            setTimeout(doLogout, 800);
          } else {
            // ตอกออกลงแล้วแต่ปลดสถานะ/คืนเคสไม่สำเร็จ → ห้าม logout
            // ไม่งั้นเคสจะค้างอยู่ในมือคนที่ออกไปแล้วโดยไม่มีใครรู้
            message.warning("ตอกออกงานแล้ว แต่ยังปลดสถานะไม่สำเร็จ กรุณากดออฟไลน์อีกครั้ง");
          }
        }
      } else {
        const res = await punchClock("in");
        if (res.success) {
          setClockState("in");
          message.success("ตอกบัตรเข้างานแล้ว");
          // ออนไลน์ให้เลย — ถ้าติดเช็คกะ/วันหยุด จะเด้ง error ของ checkIn เอง (ตอกบัตรบันทึกไปแล้ว)
          await handleCheckIn();
        }
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "ตอกบัตรไม่สำเร็จ");
    } finally {
      setIsPunching(false);
    }
  };

  // Mock data for notification and chat
  const mockNotifications = [
    { type: "warning", message: "Deposit: ไม่มีพนักงานในกะนี้" },
    { type: "danger", message: "Withdraw: มีเคสค้างเกิน 5 นาที 2 รายการ" },
    { type: "info", message: "ETC: มีเคสใหม่เข้ามา" },
  ];
  const mockChats = [
    { from: "ระบบ", text: "Withdraw: มีเคสใหม่เข้ามา" },
    { from: "ระบบ", text: "Deposit: พนักงานทั้งหมดออกจากระบบ" },
    { from: "หัวหน้า", text: "ETC: ตรวจสอบข้อมูลด่วน" },
  ];

  // แก้ไข WebSocketNotification ให้แสดง mockNotifications
  function WebSocketNotificationMock() {
    const [visible, setVisible] = useState(false);
    return (
      <>
        <Button
          type="text"
          icon={<RiNotification3Line style={{ fontSize: 22 }} />}
          onClick={() => setVisible(true)}
        />
        <Modal
          title="แจ้งเตือนระบบ (Mock)"
          open={visible}
          onCancel={() => setVisible(false)}
          footer={null}
        >
          {mockNotifications.map((item, idx) => (
            <div
              key={idx}
              style={{
                marginBottom: 12,
                color:
                  item.type === "danger"
                    ? "red"
                    : item.type === "warning"
                      ? "#faad14"
                      : "#1890ff",
              }}
            >
              {item.message}
            </div>
          ))}
        </Modal>
      </>
    );
  }

  // แก้ไขปุ่ม Chat ให้แสดง mockChats
  function ChatMock() {
    const [visible, setVisible] = useState(false);
    return (
      <>
        <Button
          type="text"
          icon={<BsChatLeft style={{ fontSize: 22 }} />}
          onClick={() => setVisible(true)}
        />
        <Modal
          title="ข้อความระบบ (Mock)"
          open={visible}
          onCancel={() => setVisible(false)}
          footer={null}
        >
          {mockChats.map((item, idx) => (
            <div key={idx} style={{ marginBottom: 12 }}>
              <b>{item.from}:</b> {item.text}
            </div>
          ))}
        </Modal>
      </>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex justify-between p-2 md:ml-6 md:mr-6 relative">
        <div className="flex items-center">
          <NavButton
            title="Menu"
            customFunc={handleActiveMenu}
            color={currentColor}
            icon={<AiOutlineMenu />}
          />

          {currentUserId && userRole === "3" && (
            <Space size="middle" className="ml-4">
              {/* ปุ่มพักกินข้าว/พักเบรค ถูกถอดออกแล้ว (เลิกใช้) — ใช้ ออนไลน์/ออฟไลน์ แทน
                  logic break เบื้องหลัง (backend + handleStartBreak/handleEndBreak) ยังอยู่ เผื่อเรียกกลับ */}

              {/* ปุ่มออนไลน์/ออฟไลน์ (สถานะพร้อมรับเคส — logic เข้างาน/ออกงานเดิม) */}
              <Button
                type="primary"
                disabled={
                  // toggle ออนไลน์/ออฟไลน์ ได้เฉพาะตอน "ตอกบัตรเข้าอยู่" เท่านั้น
                  // - ยังไม่ตอกเข้า (none) / ตอกออกไปแล้ว (done) → ล็อก (ไปใช้ปุ่มตอกบัตร)
                  //   กัน desync: ตอกบัตรออก=จบงาน แต่เดิมยังกดออฟไลน์/ออนไลน์ต่อได้
                  clockState !== "in" ||
                  // อยู่นอกกะ + ยังไม่ออนไลน์ → กดออนไลน์ไม่ได้ (แต่ถ้าออนไลน์อยู่ กดออฟไลน์ได้เสมอ)
                  (!isScheduled && workStatus !== "Ready")
                }
                icon={<LoginOutlined />}
                loading={isWorkLoading || workStatus === null}
                onClick={() => {
                  if (workStatus === "Ready") {
                    // ปุ่มออฟไลน์ = พักชั่วคราว (ยังตอกบัตรเข้าอยู่) → ส่งเคสต่อให้คนที่ทำงานอยู่
                    handleCheckOut("offline");
                  } else {
                    handleCheckIn();
                  }
                }}
                style={{
                  backgroundColor:
                    workStatus === "Ready" ? "#f5222d" : "#2196f3",
                  borderColor: workStatus === "Ready" ? "#f5222d" : "#2196f3",
                }}
              >
                {workStatus === null
                  ? "กำลังโหลด..."
                  : workStatus === "Ready"
                    ? "ออฟไลน์"
                    : "ออนไลน์"}
              </Button>
            </Space>
          )}

          {/* Employee Status Inline */}
          <EmployeeStatusInline />
        </div>

        <div className="flex items-center">
          {/* Notification Box */}
          {/* <div className="mr-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 min-w-[200px]">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-blue-700 text-sm font-medium">
                    การแจ้งเตือน
                  </span>
                </div>
              </div>
              <div className="mt-1">
                <div className="text-blue-800 text-xs truncate">
                  มีเคสใหม่รอการจัดการ
                </div>
              </div>
            </div>
          </div> */}

          {/* ปุ่มตอกบัตร เข้างาน/ออกงาน (จดเวลา + ออนไลน์/ออฟไลน์ให้อัตโนมัติ) */}
          {currentUserId && userRole === "3" && (
            <Button
              type="primary"
              icon={<FieldTimeOutlined />}
              loading={isPunching || clockState === null}
              disabled={clockState === "done"}
              onClick={handlePunch}
              style={{
                backgroundColor:
                  clockState === "in"
                    ? "#f5222d"
                    : clockState === "done"
                      ? "#8c8c8c"
                      : "#52c41a",
                borderColor:
                  clockState === "in"
                    ? "#f5222d"
                    : clockState === "done"
                      ? "#8c8c8c"
                      : "#52c41a",
              }}
            >
              {clockState === null
                ? "กำลังโหลด..."
                : clockState === "in"
                  ? "ออกงาน"
                  : clockState === "done"
                    ? "ตอกบัตรแล้ว"
                    : "เข้างาน"}
            </Button>
          )}

          <Space size="middle" className="ml-4">
            {((location.pathname.includes("/backoffice") ||
              location.pathname.includes("/admin")) && currentUserId != 0) && (
                <Button
                  type="primary"
                  icon={<TeamOutlined />}
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                  onClick={() => handleNavigation("workspace")}
                >
                  Workspace
                </Button>
              )}
            {/* เปิดบริษัทอื่นเป็นแท็บใหม่ — โชว์เฉพาะคนที่สังกัดมากกว่า 1 บริษัท */}
            {location.pathname.includes("/workspace") && myCompanies.length > 1 && (
              <Button
                type="primary"
                icon={<AppstoreOutlined />}
                style={{ backgroundColor: "#fa8c16", borderColor: "#fa8c16" }}
                onClick={() => setIsPortalVisible(true)}
              >
                บริษัทอื่น ({myCompanies.length})
              </Button>
            )}
            {location.pathname.includes("/workspace") && (
              <>
                {userRole == "1" && (
                  <Button
                    type="primary"
                    icon={<DashboardOutlined />}
                    style={{
                      backgroundColor: "#1890ff",
                      borderColor: "#1890ff",
                    }}
                    onClick={() => handleNavigation("backoffice")}
                  >
                    Backoffice
                  </Button>
                )}
                {(userRole == "2" || userRole == "1") && (
                  <Button
                    type="primary"
                    icon={<SettingOutlined />}
                    style={{
                      backgroundColor: "#722ed1",
                      borderColor: "#722ed1",
                    }}
                    onClick={() => handleNavigation("admin")}
                  >
                    Admin
                  </Button>
                )}
              </>
            )}
          </Space>

          {/* WebSocket Notification */}
          <div className="mr-4">
            {/* <WebSocketNotification /> */}
          </div>

          {/* เสียงแจ้งเตือนงานใหม่ — การ "คลิก" ตรงนี้คือ user gesture ที่เบราว์เซอร์
              ต้องการเพื่อปลดล็อกการเล่นเสียง ถ้าไม่มีปุ่มนี้เสียงจะไม่มีวันดัง */}
          <TooltipComponent
            content={
              !soundOn
                ? "เปิดเสียงแจ้งเตือน"
                : audioReady
                  ? "ปิดเสียงแจ้งเตือน"
                  : "เปิดอยู่ — คลิกที่ไหนก็ได้สักครั้งเพื่อให้เบราว์เซอร์อนุญาตเล่นเสียง"
            }
            position="BottomCenter"
          >
            <button
              type="button"
              onClick={handleToggleSound}
              className="p-2 rounded-full hover:bg-light-gray cursor-pointer"
              // เปิดแต่ยังปลดล็อกไม่ได้ = สีส้ม บอกว่ายังไม่พร้อมจริง ไม่ใช่เขียวหลอกตา
              style={{ color: !soundOn ? "#9ca3af" : audioReady ? currentColor : "#fa8c16" }}
              aria-label="เสียงแจ้งเตือนงานใหม่"
            >
              {soundOn ? <MdNotificationsActive size={20} /> : <MdNotificationsOff size={20} />}
            </button>
          </TooltipComponent>

          {/* ... Profile ... */}

          <TooltipComponent content="Profile" position="BottomCenter">
            <div
              className="flex items-center gap-2 cursor-pointer p-1 hover:bg-light-gray rounded-lg ml-4"
              onClick={() => handleClick("userProfile")}
            >
              <p>
                {/* <span className="text-gray-600 text-14"></span> */}
                <span className="text-black font-semibold text-14">
                  {userName || "User"}
                </span>{" "}
              </p>
              <MdKeyboardArrowDown className="text-gray-400 text-14" />
            </div>
          </TooltipComponent>

          {isClicked.cart && <Cart />}
          {isClicked.chat && <Chat />}
          {isClicked.notification && <Notification />}
          {isClicked.userProfile && <UserProfile />}
        </div>
      </div>

      {/* Portal เลือกบริษัท — เปิดหน้างานของแต่ละบริษัทเป็นแท็บแยก */}
      <Modal
        title="เปิดบริษัทอื่นเป็นแท็บใหม่"
        open={isPortalVisible}
        onCancel={() => setIsPortalVisible(false)}
        footer={null}
        width={520}
        destroyOnClose
      >
        <CompanyPortal compact onOpened={() => setIsPortalVisible(false)} />
      </Modal>

      {/* Company Selection Modal */}

      {currentUserId && userRole == "1" && <Modal
        title="เลือก Company"
        open={isCompanyModalVisible}
        onOk={handleCompanySelect}
        onCancel={() => {
          setIsCompanyModalVisible(false);
          setSelectedCompany(null);
          sessionStorage.setItem("location", "backoffice");
        }}
        okText="ยืนยัน"
        cancelText="ยกเลิก"
      >
        <Select
          style={{ width: "100%" }}
          placeholder="เลือก Company"
          options={companies}
          value={selectedCompany}
          onChange={(value, selectItem) => {
            setSelectedCompany(value);
            setCompanyName(selectItem.label);
          }}
        />
      </Modal>}
    </div>
  );
};

export default Navbar;
